import { Action, Dispatch } from 'redux';
import * as Actions from '../actions/eLearning';
import { IUserAttr } from '../actions/session';
import { decodeHTMLEntities } from '../utils/Text';
import AbstractScenario from './AbstractScenario';

import { IELearningParamMap } from 'src/reducers/eLearning';

/** eラーニングTopBlockデータ取得シナリオ用パラメータ */
export interface IFetchELearningTopParam {
	careerID: string;
	classID: string;
	userId: string;
	isPreview: boolean;
}

/** eラーニングProgressBlockデータ取得シナリオ用パラメータ */
export interface IFetchELearningProgressParam {
	careerID: string;
	userId: string;
	classID: string;
	isPreview: boolean;
}

/** eラーニング受講開始シナリオ用パラメータ */
export interface IStartELearningTextParam {
	careerID: string;
	classID: string;
	topicID: string;
	userAttr: IUserAttr;
	page: number;
	pageCount: number;
	isPreview: boolean;
	// 目次クリック判定
	isMenu: boolean;
}

/** eラーニング受講終了シナリオ用パラメータ */
export interface IEndELearningTextParam {
	careerID: string;
	classID: string;
	topicID: string;
	userAttr: IUserAttr;
	page: number;
	pageCount: number;
	startTime: string;
	isPreview: boolean;
}

/** eラーニングTreeBlock情報取得シナリオ用パラメータ */
export interface IFetchELearningTreeParam {
	careerID: string;
	classID: string;
	userAttr: IUserAttr;
	isPreview: boolean;
}

/** eラーニングブックマーク設定シナリオ用パラメータ */
export interface IUpdateELearningUserBookMarkParam {
	careerID: string;
	classID: string;
	topicID: string;
	userAttr: IUserAttr;
	page: number;
}

/** eラーニング次ツリー通知シナリオ用パラメータ */
export interface INextTreeParam {
	actionKind: 'next' | 'prev';
	careerID: string;
	topicID: string;
	startTime: string;
	userAttr: IUserAttr;
}

/**
 * eラーニングシナリオファクトリ
 */
export default class ELearningScenarioFactory {
	/**
	 * eラーニングTopBlockデータ取得シナリオ作成
	 */
	public static createFetchELearningTop(dispatch: Dispatch, token: string) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<
			IFetchELearningTopParam
		> {
			protected process(arg: IFetchELearningTopParam) {
				const param = {
					accessType: arg.isPreview ? 21 : 10,
					careerID: arg.careerID,
					classID: arg.classID,
					lang: '',
					userId: arg.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				return this.read('el_el_ELearningTopBlockModel', param).then(
					data => {
						const resumeTopic: string = data.resumeTopic;
						const resumePage = +data.resumePage;
						const explanationPage = +data.explanationPage;
						const lang: string = data.lang;
						const tsqId: string = data.tsqId;
						const status: number = +data.status;
						const testTitle: string = data.testTitle;
						const retries: string = data.retries;
						const cnt: string = data.cnt;
						const timeLimit: string = data.timeLimit;
						const scorm: boolean = data.scorm;
						const lastLocation: string = data.lastLocation;
						this.dispatch(
							Actions.setELearningTopBlock({
								careerID: arg.careerID,
								classID: arg.classID,
								cnt,
								explanationPage,
								lang,
								lastLocation,
								resumePage,
								resumeTopic,
								retries,
								scorm,
								status,
								testTitle,
								timeLimit,
								tsqId,
							})
						);
					}
				);
			}

			protected logError = (e: Error) => {
				// 呼び出し元でエラーハンドリングするため、そのままthrow
				throw e;
			};
		}(dispatch, token);
		return scenario;
	}

	/**
	 * eラーニングProgressBlockデータ取得シナリオ作成
	 */
	public static createFetchELearningProgress<
		A extends ReturnType<typeof Actions.setELearningProgressBlock>
	>(dispatch: Dispatch<A | Action<{}>>, token: string) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<
			IFetchELearningProgressParam,
			void,
			ReturnType<typeof Actions.setELearningProgressBlock>
		> {
			protected async process(arg: IFetchELearningProgressParam) {
				const param = {
					accessType: arg.isPreview ? 21 : 10,
					careerID: arg.careerID,
					check: false,
					classID: arg.classID,
					progress: '',
					userId: arg.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				const data = await this.read<{
					progress: string;
					isCompletion: number;
				}>('el_el_ELearningProgressBlockModel', param);
				const progress: number = +data.progress;
				const completion: number = +data.isCompletion;
				this.dispatch(
					Actions.setELearningProgressBlock({
						completion,
						progress,
					})
				);
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * eラーニング受講開始シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createStartELearningText(dispatch: Dispatch, token: string) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<
			IStartELearningTextParam
		> {
			protected process(arg: IStartELearningTextParam) {
				const param = {
					accessType: arg.isPreview ? 21 : 10,
					actionKind: 'start',
					careerID: arg.careerID,
					classID: arg.classID,
					lang: arg.userAttr.lang,
					mediaSiteApiStatus: 'initial',
					page: arg.page,
					pageCount: arg.pageCount,
					startTime: '',
					topicID: arg.topicID,
					userId: arg.userAttr.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				return this.read('el_el_ELearningTextBlockModel', param).then(
					data => {
						const startTime: string = data.startTime;
						this.dispatch(
							Actions.setELearningTextBlock({ startTime })
						);
					}
				);
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * AdmintTV情報データ取得シナリオ作成
	 * 1.Admint連携フラグ
	 * 2.動画視聴画面URL
	 * @param dispatch
	 * @param token
	 */
	public static createAdmintResult(dispatch: Dispatch, token: string) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<
			IStartELearningTextParam
		> {
			protected process(arg: IStartELearningTextParam) {
				const param = {
					accessType: arg.isPreview ? 21 : 10,
					careerID: arg.careerID,
					page: arg.page,
					pageCount: arg.pageCount,
					topicID: arg.topicID,
					userId: arg.userAttr.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				return this.read(
					'el_el_ELearningTextAdmintTicketModel',
					param
				).then(data => {
					const admintFlg: string = data.admintFlg;
					const admintUrl: string = data.admintUrl;
					this.dispatch(
						Actions.setELearningTextAdmintResult({
							admintFlg,
							admintUrl,
						})
					);
				});
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * Admint動画修了判定用シナリオ作成
	 *
	 * @param dispatch
	 * @param token
	 */
	public static createAdmintCompleteModel(dispatch: Dispatch, token: string) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<
			IStartELearningTextParam
		> {
			protected process(arg: IStartELearningTextParam) {
				const param = {
					accessType: arg.isPreview ? 21 : 10,
					careerID: arg.careerID,
					classID: arg.classID,
					isMenu: arg.isMenu,
					page: arg.page,
					pageCount: arg.pageCount,
					topicID: arg.topicID,
					userId: arg.userAttr.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				return this.read(
					'el_el_ELearningTextAdmintCompleteModel',
					param
				).then(async data => {
					const s = ELearningScenarioFactory.createFetchELearningTree(
						dispatch,
						token
					);
					await s.run({
						careerID: arg.careerID,
						classID: arg.classID,
						isPreview: false,
						userAttr: arg.userAttr,
					});
				});
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * eラーニング受講終了シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createEndELearningText(dispatch: Dispatch, token: string) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<
			IEndELearningTextParam
		> {
			protected process(arg: IEndELearningTextParam) {
				const param = {
					accessType: arg.isPreview ? 21 : 10,
					actionKind: 'end',
					careerID: arg.careerID,
					classID: arg.classID,
					id: 'update',
					lang: arg.userAttr.lang,
					mediaSiteApiStatus: 'initial',
					page: arg.page,
					pageCount: arg.pageCount,
					startTime: arg.startTime,
					topicID: arg.topicID,
					userId: arg.userAttr.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				return this.update('el_el_ELearningTextBlockModel', param).then(
					data => {
						//
					}
				);
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * eラーニングTreeBlock情報取得シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createFetchELearningTree(dispatch: Dispatch, token: string) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<
			IFetchELearningTreeParam
		> {
			protected process(arg: IFetchELearningTreeParam) {
				const param = {
					accessType: arg.isPreview ? 21 : 10,
					careerID: arg.careerID,
					classID: arg.classID,
					higherLevel: '1',
					lang: arg.userAttr.lang,
					lowerLevel: '1',
					userId: arg.userAttr.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				return this.read('el_el_ELearningTreeBlockModel', param).then(
					data => {
						const careerTitle = data.careerTitle;
						const documentName = data.documentName;
						const markAfter = data.markAfter;
						const requiredItemFlg = data.requiredItemFlg;
						const indexTree = data.treeList.map((item: any) =>
							ELearningScenarioFactory.toIndexTreeItem(item)
						);
						const mapParam: IELearningParamMap = {
							markTimingFlg: data.markTimingFlg,
							orderFlg: data.orderFlg,
						};

						this.dispatch(
							Actions.setELearningTreeBlock({
								careerTitle,
								documentName,
								indexTree,
								mapParam,
								markAfter,
								preview: arg.isPreview,
								requiredItemFlg,
							})
						);
					}
				);
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * eラーニングブックマーク設定シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createUpdateELearningUserBookMark(
		dispatch: Dispatch,
		token: string
	) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<
			IUpdateELearningUserBookMarkParam
		> {
			protected process(arg: IUpdateELearningUserBookMarkParam) {
				const param = {
					accessType: '10',
					careerID: arg.careerID,
					id: 'update',
					page: arg.page,
					topicID: arg.topicID,
					userId: arg.userAttr.userId,
					viewMode: 0,
				};
				return this.update(
					'el_el_ELearningUserBookmarkModel',
					param
				).then(data => {
					const s = ELearningScenarioFactory.createFetchELearningTree(
						dispatch,
						token
					);
					s.run({
						careerID: arg.careerID,
						classID: arg.classID,
						isPreview: false,
						userAttr: arg.userAttr,
					});
				});
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * eラーニング次ツリー通知シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createNextTree(dispatch: Dispatch, token: string) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<INextTreeParam> {
			protected process(arg: INextTreeParam) {
				const param = {
					actionKind: arg.actionKind,
					careerID: arg.careerID,
					lang: arg.userAttr.lang,
					screenKind: 'TEX',
					startTime: arg.startTime,
					topicID: arg.topicID,
					userId: arg.userAttr.userId,
					viewMode: 0,
				};
				return this.read('el_el_NextTreeModel', param);
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * AIロープレ情報データ取得シナリオ作成
	 * 1.AIロープレフラグ
	 * 2.AIロープレURL
	 * 3.AIロープレ生成URL
	 * @param dispatch
	 * @param token
	 */
	public static createAiRolePlayResult(dispatch: Dispatch, token: string) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<
			IStartELearningTextParam
		> {
			protected process(arg: IStartELearningTextParam) {
				const param = {
					accessType: arg.isPreview ? 21 : 10,
					careerID: arg.careerID,
					page: arg.page,
					pageCount: arg.pageCount,
					topicID: arg.topicID,
					userId: arg.userAttr.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				return this.read(
					'el_el_ELearningTextAiRolePlayTicketModel',
					param
				).then(data => {
					const aiRolePlayFlg: string = data.aiRolePlayFlg;
					const aiRolePlayUrl: string = data.aiRolePlayUrl;
					this.dispatch(
						Actions.setElearningTextAiRolePlayResult({
							aiRolePlayFlg,
							aiRolePlayUrl,
						})
					);
				});
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * AIロープレ結果データ取得シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createAiRolePlayComplete(dispatch: Dispatch, token: string) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<
			IStartELearningTextParam
		> {
			protected process(arg: IStartELearningTextParam) {
				const param = {
					accessType: arg.isPreview ? 21 : 10,
					careerID: arg.careerID,
					classID: arg.classID,
					page: arg.page,
					pageCount: arg.pageCount,
					topicID: arg.topicID,
					userId: arg.userAttr.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				return this.read(
					'el_el_ELearningTextAiRolePlayCompleteModel',
					param
				).then(data => {
					const aiRolePlayErrMessage: string =
						data.aiRolePlayErrMessage;
					const aiRolePlayResponseCode: number =
						data.aiRolePlayResponseCode;
					this.dispatch(
						Actions.setElearningTextAiRolePlayResponse({
							aiRolePlayErrMessage,
							aiRolePlayResponseCode,
						})
					);
				});
			}
		}(dispatch, token);
		return scenario;
	}

	// IIndexTreeItem型変換
	protected static toIndexTreeItem = (from: any) => {
		const to: Actions.IIndexTreeItem = {
			answerPeriod: decodeHTMLEntities(from.answerPeriod),
			children: null,
			cnt: from.cnt,
			completeFlg: from.completeFlg,
			disableFlg: from.disableFlg,
			displayStyle: from.displayStyle,
			enqueteType: from.enqueteType,
			expired: from.expired,
			hintList: null,
			iconClass: from.iconClass,
			id: from.id,
			indexNo: from.indexNo,
			kind: from.kind,
			notMarkFlg: from.notMarkFlg,
			page: from.page,
			pageCount: from.pageCount,
			requiredFlg: from.requiredFlg !== 0,
			retries: from.retries,
			scoType: from.scoType,
			seqNo: from.seqNo,
			subjectText: from.subjectText,
			testID: from.testID || undefined,
			testTitle: from.testTitle,
			text: from.text,
			timeLimit: from.timeLimit,
			userBookMark: +from.userBookMark,
		};
		if (from.hintList) {
			const hintList: { [lang: string]: number[] } = {};
			for (const key of Object.keys(from.hintList)) {
				// jaHintPageList、enHintPageList...でくるので置き換え
				const hintLang = key.replace(/HintPageList$/, '');
				hintList[hintLang] = from.hintList[key];
			}
			to.hintList = hintList;
		}
		if (from.children) {
			to.children = from.children.map((child: any) =>
				ELearningScenarioFactory.toIndexTreeItem(child)
			);
		}
		return to;
	};
}



// WEBPACK FOOTER //
// ./src/scenario/ELearningScenarioFactory.ts